import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, Dumbbell, Pencil, Plus, RefreshCw } from 'lucide-react';
import api from '../../services/api';

export default function D28dRoutineLibraryBox({
  canEdit = false,
  selectedRoutineId = '',
  refreshToken = 0,
  gymScoped = false,
  requestParams = {},
  onSelectForClass,
  onEditRoutine,
  onCreateRoutine,
  onImportCatalog,
}) {
  const [open, setOpen] = useState(false);
  const [routines, setRoutines] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCategoria, setFilterCategoria] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const params = filterCategoria
        ? { categoria: filterCategoria, estado: 'activa', ...requestParams }
        : { estado: 'activa', ...requestParams };
      const [listRes, catRes] = await Promise.all([
        api.get('/d28d/routines/schedule', { params: requestParams }).catch(() => api.get('/d28d/routines', { params })),
        api.get('/d28d/routines/categories', { params: requestParams }),
      ]);
      setRoutines(listRes.data?.data || []);
      setCategories(catRes.data?.data || []);
      setLoadedOnce(true);
    } catch (e) {
      setRoutines([]);
      setLoadError(e?.response?.data?.message || 'No se pudieron cargar las rutinas.');
    } finally {
      setLoading(false);
    }
  }, [filterCategoria, requestParams]);

  useEffect(() => {
    if (open || refreshToken > 0) load();
  }, [load, refreshToken, open]);

  const categoryNames = useMemo(() => categories.map((c) => c.nombre), [categories]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return routines;
    return routines.filter((r) => {
      const hay = [r.nombre, r.categoria, r.subcategoria, r.objetivo, r.nivel]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [routines, search]);

  const selectedRoutine = useMemo(
    () => routines.find((r) => String(r.id) === String(selectedRoutineId)),
    [routines, selectedRoutineId],
  );

  const handleUseInClass = (routine) => {
    onSelectForClass?.(routine);
    setOpen(false);
  };

  const handleCreate = () => {
    onCreateRoutine?.();
    setOpen(false);
  };

  return (
    <section className="d28d-routine-library mb-4">
      <button
        type="button"
        className={`d28d-routine-library__toggle ${open ? 'd28d-routine-library__toggle--open' : ''}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="d28d-routine-library__toggle-main">
          <Dumbbell className="w-4 h-4 shrink-0" />
          <span className="font-semibold">{gymScoped ? 'Rutinas del gimnasio' : 'Rutinas D28D'}</span>
          {loadedOnce && !loading ? (
            <span className="d28d-routine-library__count">{routines.length}</span>
          ) : null}
          {selectedRoutine ? (
            <span className="d28d-routine-library__selected truncate">
              · {selectedRoutine.nombre}
            </span>
          ) : (
            <span className="d28d-routine-library__hint truncate">
              · Ver, crear y editar plantillas
            </span>
          )}
        </span>
        <ChevronDown className={`w-5 h-5 shrink-0 d28d-routine-library__chevron ${open ? 'd28d-routine-library__chevron--open' : ''}`} />
      </button>

      {open ? (
        <div className="d28d-routine-library__panel ui-light-panel surface-readable space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <p className="text-sm text-slate-600 m-0">
              Pulsa «Usar en clase» para vincular la rutina al formulario de abajo.
            </p>
            <div className="flex flex-wrap gap-2">
              <button type="button" className="btn-secondary text-sm inline-flex items-center gap-1" onClick={load} disabled={loading}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </button>
              {canEdit && onImportCatalog ? (
                <button type="button" className="btn-secondary text-sm" onClick={onImportCatalog}>
                  Importar catálogo
                </button>
              ) : null}
              {canEdit && onCreateRoutine ? (
                <button type="button" className="btn-primary text-sm inline-flex items-center gap-1" onClick={handleCreate}>
                  <Plus className="w-4 h-4" />
                  Nueva rutina
                </button>
              ) : null}
            </div>
          </div>

          {loadError ? (
            <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{loadError}</p>
          ) : null}

          <div className="flex flex-wrap gap-3">
            <label className="text-sm min-w-[10rem] flex-1">
              <span className="ui-field__label">Buscar rutina</span>
              <input
                className="ui-input mt-1"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nombre, categoría, objetivo…"
              />
            </label>
            <label className="text-sm min-w-[10rem]">
              <span className="ui-field__label">Categoría</span>
              <select className="ui-input mt-1" value={filterCategoria} onChange={(e) => setFilterCategoria(e.target.value)}>
                <option value="">Todas</option>
                {categoryNames.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
          </div>

          {loading ? (
            <p className="text-sm text-slate-500 py-4">Cargando rutinas…</p>
          ) : filtered.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
              <p className="text-sm text-slate-600">
                {routines.length === 0
                  ? (gymScoped
                    ? 'Aún no hay rutinas activas. Crea la primera plantilla para tu gimnasio.'
                    : 'Aún no hay rutinas activas. Crea la primera o importa el catálogo D28D.')
                  : 'Ninguna rutina coincide con el filtro.'}
              </p>
              {canEdit && routines.length === 0 && onCreateRoutine ? (
                <button type="button" className="btn-primary text-sm mt-3 inline-flex items-center gap-1" onClick={handleCreate}>
                  <Plus className="w-4 h-4" />
                  Crear rutina
                </button>
              ) : null}
            </div>
          ) : (
            <div className="d28d-routine-library__grid">
              {filtered.map((r) => {
                const isSelected = String(selectedRoutineId) === String(r.id);
                return (
                  <article
                    key={r.id}
                    className={`d28d-routine-library__card ${isSelected ? 'd28d-routine-library__card--selected' : ''}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-900 truncate">{r.nombre}</p>
                      <p className="text-xs text-slate-600 mt-0.5">
                        {r.categoria}
                        {r.subcategoria ? ` · ${r.subcategoria}` : ''}
                        {' · '}
                        v{r.version}
                        {r.duracion ? ` · ${r.duracion}` : ''}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-1.5 shrink-0">
                      <button
                        type="button"
                        className="btn-primary text-xs py-1.5 px-2.5"
                        onClick={() => handleUseInClass(r)}
                      >
                        Usar en clase
                      </button>
                      {canEdit && onEditRoutine ? (
                        <button
                          type="button"
                          className="btn-secondary text-xs py-1.5 px-2.5 inline-flex items-center gap-1"
                          onClick={() => onEditRoutine(r.id)}
                        >
                          <Pencil className="w-3 h-3" />
                          Editar
                        </button>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}
